import { useFeatureIsOn } from '@growthbook/growthbook-react'
import TestRunCard from 'components/dashboard/test-run-card'
import DatetimeDisplay from 'components/datetime-display'
import DurationDisplay from 'components/duration-display'
import ProjectSelect from 'components/form/project-select'
import TestSuiteSelect from 'components/form/test-suite-select'
import TestRunMultiStatusSelect from 'components/form/testrun-multi-status-select'
import IconButton from 'components/icon-button'
import Spinner from 'components/spinner'
import TestFilters from 'components/test-filters'
import TestRunProgress from 'components/test-run-progress'
import TestSuiteLink from 'components/test-suite-link'
import { calculateDuration } from 'core/date-utils'
import { setPageInfo, useInitialEffect, useUnloadEffect } from 'core/react-utils'
import * as Storage from 'core/storage'
import { buildRefUrl, isBlank, organizationUrl } from 'core/utils'
import { formatISO, parseISO } from 'date-fns'
import { runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { completedTestRunStatuses, TestRunStatus } from 'models/test-run'
import Page from 'pages/page'
import { useEffect, useState } from 'react'
import DatePicker from 'react-datepicker'
import { Link, useHistory } from 'react-router-dom'
import { Button, Dropdown, Icon, Input, Popup, Table } from 'semantic-ui-react'
import {
  AppStore,
  ModalStore,
  PipelineStore,
  ProjectStore,
  ReviewStore,
  SettingsStore,
  TestRunStore,
  TestSuiteStore,
  TriggerStore,
  UserStore
} from 'stores'
import BranchMultiSelect from '../../components/form/branch-multi-select'
import EnvironmentMultiSelect from '../../components/form/environment-multi-select'
import PipelineStageMultiSelect from '../../components/form/pipeline-stage-multi-select'
import TestRunReviewerSelect from '../../components/form/test-run-reviewer-select'
import { ReviewType } from '../../models/review-data'
import './test-runs.scss'

const TestRunsPage = observer(() => {
  const [activePage, setActivePage] = useState(1)
  const [selectedIndex, setSelectedIndex] = useState<number>(0)
  const [showHelp, setShowHelp] = useState(false)
  const [statusFilterOpen, setStatusFilterOpen] = useState(false)
  const [dateFilterOpen, setDateFilterOpen] = useState(false)
  const [pipelineStageFilterOpen, setPipelineStageFilterOpen] = useState(false)
  const [projectFilterOpen, setProjectFilterOpen] = useState(false)
  const [branchFilterOpen, setBranchFilterOpen] = useState(false)
  const [reviewerFilterOpen, setReviewerFilterOpen] = useState(false)
  const [assignDropdownOpen, setAssignDropdownOpen] = useState(false)
  const [reviewerSearchQuery, setReviewerSearchQuery] = useState('')
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set())
  const history = useHistory()
  const allowUploadEnabled = useFeatureIsOn('allow-upload-test-run-override')

  setPageInfo('testruns', 'Test Runs')

  const handleQueryParams = async (): Promise<void> => {
    let params = new URLSearchParams(window.location.search)
    const storedParams = new URLSearchParams(Storage.getTestRunsTabFilters())
    if (isBlank(params.toString()) && storedParams.size > 0) {
      params = storedParams
      history.replace(organizationUrl(`test-runs?${params.toString()}`))
    }

    const environmentParam = params.get('environment')
    const filterEnvironmentIdParam = environmentParam !== null
      ? environmentParam.split(',').map((env) => Number(env))
      : []
    const pipelineStageParam = params.get('pipelineStage')
    const filterPipelineStageIdParam = pipelineStageParam !== null
      ? pipelineStageParam.split(',').map((ps) => Number(ps))
      : []
    const filterProjectIdParam = parseInt(params.get('project') ?? '0')
    const branchParam = params.get('branch')
    const filterBranchParam = branchParam !== null ? branchParam.split(',') : []
    const filterSuiteIdParam = parseInt(params.get('suite') ?? '0')
    const reviewerParam = params.get('reviewer')
    const filterReviewerIdParam = reviewerParam !== null
      ? reviewerParam.split(',').map((k) => parseInt(k))
      : null
    const statusParam = params.get('status')
    const filterStatusIdParam: TestRunStatus[] = statusParam !== null
      ? statusParam.split(',').map((k) => TestRunStatus[k])
      : []
    const dateParam = params.get('date')
    const filterDateParam = dateParam !== null ? parseISO(dateParam) : null
    TestRunStore.setFilterProject(filterProjectIdParam)
    TestRunStore.updateFilterBranch(filterBranchParam)
    TestRunStore.setFilterTestSuite(filterSuiteIdParam)
    TestRunStore.setFilterStatus(filterStatusIdParam)
    await UserStore.ensureUserRoles()
    if (
      (filterReviewerIdParam !== null && filterReviewerIdParam.length > 0) ||
      TestRunStore.filterReviewerIds.length > 0
    ) {
      TestRunStore.setFilterReviewerIds(filterReviewerIdParam ?? [])
    }
    if (filterDateParam !== null || TestRunStore.filterDate !== null) {
      TestRunStore.updateFilterDate(filterDateParam)
    }
    if (
      filterEnvironmentIdParam.length > 0 ||
      TestRunStore.filterEnvironmentIds.length > 0
    ) {
      TestRunStore.updateFilterEnvironment(filterEnvironmentIdParam)
    }
    if (
      filterPipelineStageIdParam.length > 0 ||
      TestRunStore.filterPipelineStageIds.length > 0
    ) {
      TestRunStore.updateFilterPipelineStage(filterPipelineStageIdParam)
    }
    if (
      filterBranchParam.length > 0 ||
      TestRunStore.filterBranches.length > 0
    ) {
      TestRunStore.updateFilterBranch(filterBranchParam)
    }
  }

  useInitialEffect(async (): Promise<void> => {
    await SettingsStore.ensureEnvironments()
    await SettingsStore.loadAuthorizations()
    await ProjectStore.ensureLoaded()
    await PipelineStore.ensureLoaded()
    await TestRunStore.ensureLoaded(true)
    await AppStore.loadAccount()
    await UserStore.loadUserRoles()
    await ReviewStore.loadReviewStatuses()
  })

  useUnloadEffect(() => {
    void TestRunStore.ensureLoaded(false)
  })

  useEffect((): void => {
    void (async (): Promise<void> => {
      await handleQueryParams()
      await SettingsStore.ensureEnvironments()
      await ProjectStore.ensureLoaded()
      await TriggerStore.ensureLoaded()
      await TestRunStore.ensureLoaded(true)
    })()
  }, [TestRunStore.testRunsLoaded]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      // Handle Esc to close help menu
      if (e.key === 'Escape' && showHelp) {
        e.preventDefault()
        setShowHelp(false)
        return
      }

      // Ignore if user is typing in an input/textarea
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return
      }

      if (e.key === '?') {
        e.preventDefault()
        setShowHelp(true)
      } else if (e.key === 'j') {
        e.preventDefault()
        setSelectedIndex((prev) => Math.min(prev + 1, TestRunStore.testRuns.length - 1))
      } else if (e.key === 'k') {
        e.preventDefault()
        setSelectedIndex((prev) => Math.max(prev - 1, 0))
      } else if (e.key === 'v' || e.key === 'Enter') {
        e.preventDefault()
        if (selectedIndex < TestRunStore.testRuns.length) {
          const testRun = TestRunStore.testRuns[selectedIndex]
          history.push(organizationUrl(`test-runs/${testRun.id}`))
        }
      } else if (e.key === 'r' && e.ctrlKey) {
        e.preventDefault()
        if (selectedIndex < TestRunStore.testRuns.length && AppStore.account.isLegacyOrTierTwoPlusPlan() === true) {
          const testRun = TestRunStore.testRuns[selectedIndex]
          const completed = completedTestRunStatuses.includes(testRun.status)
          if (completed) {
            void TestRunStore.rerunTestRun(testRun)
            void handlePageChange(0)
          }
        }
      } else if (e.key === 'a') {
        e.preventDefault()
        if (selectedIndex < TestRunStore.testRuns.length) {
          setAssignDropdownOpen((prev) => !prev)
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => { window.removeEventListener('keydown', handleKeyDown) }
  }, [selectedIndex, history, showHelp]) // eslint-disable-line react-hooks/exhaustive-deps

  // Reset selected index when test runs change (filters, page changes, etc.)
  useEffect(() => {
    setSelectedIndex(0)
  }, [TestRunStore.testRuns.length, activePage]) // eslint-disable-line react-hooks/exhaustive-deps

  const toggleCardExpanded = (id: number): void => {
    setExpandedCards(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const newTestRunButton = (
    <Button positive={true} onClick={() => { ModalStore.newTestRun.show() }}>
      <Icon name="plus" />
      New Test Run
    </Button>
  )

  const uploadTestRunButton = (
    <Button positive={true} onClick={() => { ModalStore.uploadTestRun.show() }}>
      <Icon name="plus" />
      Upload Test Run
    </Button>
  )

  const tableBody: JSX.Element[] = []
  loadTableBody()

  function loadTableBody (): void {
    TestRunStore.testRuns.forEach((testRun, index) => {
      const project = testRun.project
      const completed = completedTestRunStatuses.includes(testRun.status)
      const canceled = testRun.status === TestRunStatus.CANCELED
      const linkToTestRun = organizationUrl(`test-runs/${testRun.id}`)
      const linkToEnvironment = organizationUrl(`environments/${testRun.environment?.id}`)
      const linkToTrigger = organizationUrl(`triggers/${testRun.triggeredTestRunId}`)
      const trigger = testRun.triggeredTestRunId !== 0 ? TriggerStore.find(testRun.triggeredTestRunId) : null
      const refLink = buildRefUrl(project, testRun)
      const triggerRefLink = buildRefUrl(ProjectStore.find(testRun.deploy?.projectId), testRun.deploy?.commit)
      const hasBranch = !isBlank(testRun.branch)
      const hasRepo = project != null && project.hasRepo
      const allowRerunFailed = testRun.status === TestRunStatus.FAIL && completed

      let status = ''
      if (testRun.status === TestRunStatus.PASS) status = 'pass'
      if (testRun.status === TestRunStatus.FAIL) status = 'fail'

      tableBody.push(
        <Table.Row
          key={testRun.id}
          onClick={() => { history.push(linkToTestRun) }}
          className={selectedIndex === index ? 'selected' : ''}
        >
          <Table.Cell className={`status-bar-indicator ${status}`} />
          <Table.Cell className={'date-column'}>
            <div>
              <Link onClick={(e) => { e.stopPropagation() }} to={linkToTestRun}>
                <DatetimeDisplay datetime={testRun.createdAt} />
              </Link>
            </div>
            <div className="duration">
              {completed && !canceled && testRun.totalCount > 0 && (
                <span>
                  {Math.floor((testRun.passCount * 100) / (testRun.totalCount - testRun.ignoredCount))}% passing in{' '}
                </span>
              )}
              <DurationDisplay duration={calculateDuration(testRun.startTime, testRun.endTime)} />
            </div>
          </Table.Cell>
          {testRun.status == null && <Table.Cell></Table.Cell>}
          {testRun.status != null && (
            <Table.Cell className={`status-column status-${testRun.status.toLowerCase()}`}>
              <div className="status">
                {testRun.status === TestRunStatus.RUNNING && <Spinner noText={true} size={18} />}
                {testRun.statusDisplay}
              </div>
              {!isBlank(testRun.statusInfo) && testRun.statusInfo.includes('<testerylink>')
                ? (
                <Link
                  className="info"
                  onClick={(e) => { e.stopPropagation() }}
                  target="_blank"
                  rel="noopener noreferrer"
                  to={{
                    pathname: testRun.statusInfo.substring(
                      testRun.statusInfo.indexOf('<testerylink>') + '<testerylink>'.length,
                      testRun.statusInfo.lastIndexOf('</testerylink>')
                    )
                  }}
                >
                  <div
                    dangerouslySetInnerHTML={{
                      __html: testRun.statusInfo.substring(
                        testRun.statusInfo.indexOf('<testerytext>') + '<testerytext>'.length,
                        testRun.statusInfo.lastIndexOf('</testerytext>')
                      )
                    }}
                  />
                </Link>
                  )
                : (
                <div className="info">{testRun.statusInfo}</div>
                  )}
            </Table.Cell>
          )}
          <Table.Cell className={'general-info'}>
            {project != null && (
              <div>
                <Link
                  onClick={(e) => { e.stopPropagation() }}
                  to={organizationUrl(`projects/${project.key}`)}
                  title="Project"
                >
                  {testRun.project.name}
                </Link>
              </div>
            )}
            {testRun.testSuite != null && (
              <div className="test-suite-info" title="Suite">
                <TestSuiteLink testSuite={testRun.testSuite} />
              </div>
            )}
          </Table.Cell>
          <Table.Cell className="score">
            <TestRunProgress
              pass={testRun.passCount}
              fail={testRun.failCount}
              total={testRun.totalCount - testRun.ignoredCount}
              showPassPercentage={false}
            />
          </Table.Cell>
          <Table.Cell className={'code-column'}>
            {testRun.build != null && <div title="Build">{testRun.build.name}</div>}
            {trigger != null && trigger.name !== '' && (
              <div title="Trigger">
                <Link onClick={(e) => { e.stopPropagation() }} to={linkToTrigger}>
                  {trigger.name}
                </Link>
              </div>
            )}
            {hasRepo && hasBranch && <div title="Branch">{testRun.branch}</div>}
            {!isBlank(refLink ?? '') && (isBlank(triggerRefLink ?? '') || triggerRefLink === refLink) && (
              <div title="GitRef">
                <a onClick={(e) => { e.stopPropagation() }} href={refLink ?? undefined} target={'_blank'} rel="noreferrer">
                  {testRun.gitRef.substring(0, 7)}
                </a>
              </div>
            )}
            {!isBlank(triggerRefLink ?? '') && triggerRefLink !== refLink && (
              <div title="Triggered By">
                <a onClick={(e) => { e.stopPropagation() }} href={triggerRefLink ?? undefined} target={'_blank'} rel="noreferrer">
                  {testRun.deploy.commit.substring(0, 7)}
                </a>
              </div>
            )}
          </Table.Cell>
          <Table.Cell className={'environment-column'}>
            {!isBlank(testRun.environment?.pipelineStage?.name ?? '') && (
              <div style={{ marginBottom: '3px' }} title="Pipeline Stage">
                {testRun.environment?.pipelineStage?.name}
              </div>
            )}
            {!isBlank(testRun.environmentName ?? '') && (
              <Link onClick={(e) => { e.stopPropagation() }} to={linkToEnvironment} title="Environment">
                {testRun.environmentName}
              </Link>
            )}
          </Table.Cell>
          <Table.Cell className={'tagsAndFilters'}>
            <TestFilters
              includeTags={testRun.includeTags}
              excludedTags={testRun.excludedTags}
              testFilters={testRun.testFilters}
              fade={false}
              testSelectionApplied={testRun.applyTestSelectionRules}
            />
          </Table.Cell>
          <Table.Cell className={'actions'} onClick={(e) => e.stopPropagation()}>
            <div>
              <Dropdown
                open={selectedIndex === index && assignDropdownOpen}
                onOpen={() => {
                  setSelectedIndex(index)
                  setAssignDropdownOpen(true)
                  setReviewerSearchQuery('')
                }}
                onClose={() => {
                  setAssignDropdownOpen(false)
                  setReviewerSearchQuery('')
                }}
                trigger={
                  <Icon
                    name={testRun.reviewerUserId !== 0 && testRun.reviewerUserId != null ? 'user' : 'user plus'}
                    color={testRun.reviewerUserId !== 0 && testRun.reviewerUserId != null ? 'green' : undefined}
                    title={testRun.reviewerUserId !== 0 && testRun.reviewerUserId != null ? UserStore.getUser(testRun.reviewerUserId)?.fullName : undefined}
                  />
                }
                icon={null}
                direction={'left'}
              >
                <Dropdown.Menu>
                  <Dropdown.Header content={'Assign Reviewer'} />
                  <Dropdown.Divider />
                  <div style={{ padding: '0.78571429rem 1.14285714rem' }}>
                    Assigned: <strong>{testRun.reviewerUserId != null && testRun.reviewerUserId !== 0
                    ? (UserStore.getUser(testRun.reviewerUserId)?.fullName ?? 'Unknown')
                    : 'None'}</strong>
                  </div>
                  <Dropdown.Divider />
                  <div style={{ padding: '6px 8px' }} onClick={(e) => { e.stopPropagation() }}>
                    <Input
                      icon='search'
                      iconPosition='left'
                      placeholder='Search users...'
                      value={reviewerSearchQuery}
                      onChange={(e) => { setReviewerSearchQuery(e.target.value) }}
                      onBlur={(e) => { e.stopPropagation() }}
                      size='small'
                      fluid
                    />
                  </div>
                  <Dropdown.Divider />
                  <Dropdown.Item
                    icon={testRun.reviewerUserId == null || testRun.reviewerUserId === 0 ? 'check' : 'close'}
                    content={'None'}
                    onClick={() => {
                      void ReviewStore.updateAssignedReviewer({
                        reviewType: ReviewType.TEST_RUN,
                        testRunId: testRun.id,
                        testRunTestId: undefined,
                        assigneeUserId: undefined
                      }).then(() => {
                        runInAction(() => { testRun.reviewerUserId = undefined })
                        setAssignDropdownOpen(false)
                      })
                    }}
                  />
                  {UserStore.usersForDropdown
                    .filter((user) => reviewerSearchQuery === '' || user.fullName.toLowerCase().includes(reviewerSearchQuery.toLowerCase()))
                    .slice(0, 10)
                    .map((user) => (
                      <Dropdown.Item
                        key={user.id}
                        icon={testRun.reviewerUserId === user.id ? 'check' : null}
                        content={user.fullName}
                        onClick={() => {
                          void ReviewStore.updateAssignedReviewer({
                            reviewType: ReviewType.TEST_RUN,
                            testRunId: testRun.id,
                            testRunTestId: undefined,
                            assigneeUserId: user.id
                          }).then(() => {
                            runInAction(() => { testRun.reviewerUserId = user.id })
                            setAssignDropdownOpen(false)
                          })
                        }}
                      />
                    ))}
                </Dropdown.Menu>
              </Dropdown>
              <IconButton
                icon="clone"
                label="Compare"
                onClick={() => {
                  if (TestRunStore.testRunComparison1 != null) {
                    TestRunStore.updateTestRunComparison2(testRun)
                  } else {
                    TestRunStore.updateTestRunComparison1(testRun)
                  }
                }}
              />
              {AppStore.account.isLegacyOrTierTwoPlusPlan() === true && (
                <>
                  {completed
                    ? (
                    <>
                      {allowRerunFailed
                        ? (
                        <Dropdown icon={'redo'} direction={'left'}>
                          <Dropdown.Menu>
                            <Dropdown.Header content={'Rerun Options'} />
                            <Dropdown.Divider />
                            <Dropdown.Item
                              icon={'redo'}
                              content={'Rerun Test Run'}
                              onClick={() => {
                                void TestRunStore.rerunTestRun(testRun)
                                void handlePageChange(0)
                              }}
                            />
                            <Dropdown.Item
                              icon={'undo'}
                              content={'Rerun Failed Tests'}
                              onClick={() => { void TestRunStore.rerunFailedTests(testRun.id) }}
                            />
                          </Dropdown.Menu>
                        </Dropdown>
                          )
                        : (
                        <IconButton
                          icon="redo"
                          label="Rerun"
                          onClick={() => {
                            void TestRunStore.rerunTestRun(testRun)
                            void handlePageChange(0)
                          }}
                        />
                          )}
                    </>
                      )
                    : (
                    <IconButton
                      icon="stop circle outline"
                      label="Cancel"
                      onClick={async () => { await TestRunStore.cancelTestRun(testRun) }}
                    />
                      )}
                </>
              )}
              <a href={linkToTestRun} target="_blank" rel="noreferrer">
                <Icon title="Open test run in new window" name="external alternate" />
              </a>
            </div>
          </Table.Cell>
        </Table.Row>
      )
    })
  }

  const projectOptions: Array<{ key: number, text: string, value: number }> = []
  ProjectStore.active.forEach((proj) => {
    projectOptions.push({ key: proj.id, text: proj.name, value: proj.id })
  })

  const handlePageChange = async (direction: number): Promise<void> => {
    const newPage = direction === 0 ? 1 : activePage + direction
    setActivePage(newPage)
    void TestRunStore.loadPageChange(newPage).then(() => {
      loadTableBody()
    })
  }

  const onChangeProject = (projectId: number): void => {
    if (ProjectStore.loaded) {
      setActivePage(1)
      updateHistory('project', projectId?.toString())
      TestRunStore.updateFilterProject(projectId)
      if (projectId === 0 || projectId == null) onChangeBranch([])
    }
  }

  const onChangeEnvironment = (environmentIds: number[]): void => {
    setActivePage(1)
    updateHistory('environment', environmentIds?.join(','))
    TestRunStore.updateFilterEnvironment(environmentIds)
  }

  const onChangePipelineStage = (pipelineStageIds: number[]): void => {
    setActivePage(1)
    updateHistory('pipelineStage', pipelineStageIds?.join(','))
    TestRunStore.updateFilterPipelineStage(pipelineStageIds)
  }

  const onChangeBranch = (branches: string[]): void => {
    setActivePage(1)
    updateHistory('branch', branches?.join(','))
    TestRunStore.updateFilterBranch(branches)
  }

  const onChangeStatus = (statuses: TestRunStatus[]): void => {
    setActivePage(1)
    updateHistory('status', statuses.join(','))
    TestRunStore.updateFilterStatus(statuses)
  }

  const onChangeReviewer = (reviewerIds: number[]): void => {
    setActivePage(1)
    updateHistory('reviewer', reviewerIds.join(','))
    TestRunStore.updateFilterReviewerIds(reviewerIds)
  }

  const onChangeTestSuite = (testSuiteId: number): void => {
    if (TestSuiteStore.loaded) {
      setActivePage(1)
      updateHistory('suite', testSuiteId?.toString())
      TestRunStore.updateFilterTestSuite(testSuiteId)
    }
  }

  const onChangeDate = (date: Date): void => {
    setActivePage(1)
    updateHistory('date', date != null ? formatISO(date) : null)
    TestRunStore.updateFilterDate(date)
  }

  const updateHistory = (newKey: string, newValue: string | null): void => {
    const params = new URLSearchParams(window.location.search)
    if (params.toString().includes('null')) params.delete('null')

    if (newValue != null && !isBlank(newValue)) {
      params.set(newKey, newValue)
    } else {
      params.delete(newKey)
    }
    if (params.size > 0) Storage.setTestRunsTabFilters(params.toString())
    else Storage.clearTestRunsTabFilters()
    history.replace(organizationUrl(`test-runs?${params.toString()}`))
  }

  const linkToTestRunComparisonReport = (): void => {
    if (TestRunStore.testRunComparison1 === null || TestRunStore.testRunComparison2 === null) return
    history.push(
      `/testery/dashboard/analytics/test-run-comparison?test_run_ids=${TestRunStore.testRunComparison1.id},${TestRunStore.testRunComparison2.id}`
    )
  }

  const clearFilters = (): void => {
    const params = new URLSearchParams(window.location.search)
    params.forEach((_value, key) => { updateHistory(key, null) })
    void handleQueryParams()
  }

  return (
    <Page
      name="testruns"
      title="Test Runs"
      additionalCssClass={TestRunStore.loading ? ' no-scroll' : undefined}
      noHeader={true}
    >
      {AppStore.account.isLegacyOrTierTwoPlusPlan() === true && <div style={{ marginBottom: '16px' }}>{newTestRunButton}</div>}
      {(allowUploadEnabled || AppStore.account.isLegacyOrTierTwoPlusPlan() !== true) && (
        <div style={{ marginBottom: '16px' }}>{uploadTestRunButton}</div>
      )}
      <div className="comparison-container">
        {TestRunStore.testRuns.length > 1 && (
          <div>
            <div className="ui grid">
              {TestRunStore.testRunComparison1 != null && (
                <div className="eight wide column">
                  <TestRunCard key={TestRunStore.testRunComparison1.id} testRun={TestRunStore.testRunComparison1} />
                </div>
              )}
              {TestRunStore.testRunComparison2 != null && (
                <div className="eight wide column">
                  <TestRunCard key={TestRunStore.testRunComparison2.id} testRun={TestRunStore.testRunComparison2} />
                </div>
              )}
            </div>
            {TestRunStore.testRunComparison1 != null && TestRunStore.testRunComparison2 != null && (
              <Button
                className="ui positive button"
                color="green"
                onClick={() => {
                  linkToTestRunComparisonReport()
                }}
              >
                Compare Test Runs
              </Button>
            )}
          </div>
        )}
      </div>
      <div className="testRuns">
        <Table selectable={true} striped={true} unstackable={true}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell className={'status-bar-indicator'} />
              <Table.HeaderCell className={'date-column'}>
                Time
                <Popup
                  trigger={
                    <Icon
                      name="filter"
                      className={`filter-icon ${TestRunStore.filterDate != null ? 'active' : 'inactive'}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        setDateFilterOpen(!dateFilterOpen)
                      }}
                    />
                  }
                  content={
                    <div style={{ minWidth: '250px', padding: '8px' }}>
                      <DatePicker
                        isClearable
                        selected={TestRunStore.filterDate}
                        onChange={onChangeDate}
                        placeholderText="Any Date"
                        inline
                      />
                    </div>
                  }
                  on="click"
                  open={dateFilterOpen}
                  onClose={() => { setDateFilterOpen(false) }}
                  onOpen={() => { setDateFilterOpen(true) }}
                  position="bottom left"
                />
              </Table.HeaderCell>
              <Table.HeaderCell className={'status-column'}>
                Status
                <Popup
                  trigger={
                    <Icon
                      name="filter"
                      className={`filter-icon ${TestRunStore.filterStatus?.length > 0 ? 'active' : 'inactive'}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        setStatusFilterOpen(!statusFilterOpen)
                      }}
                    />
                  }
                  content={
                    <div style={{ minWidth: '250px', padding: '8px' }}>
                      <TestRunMultiStatusSelect
                        value={TestRunStore.filterStatus}
                        placeholder="Any Status"
                        onChange={onChangeStatus}
                      />
                    </div>
                  }
                  on="click"
                  open={statusFilterOpen}
                  onClose={() => { setStatusFilterOpen(false) }}
                  onOpen={() => { setStatusFilterOpen(true) }}
                  position="bottom left"
                />
              </Table.HeaderCell>
              <Table.HeaderCell className={'general-info'}>
                Test Run
                <Popup
                  trigger={
                    <Icon
                      name="filter"
                      className={`filter-icon ${(TestRunStore.filterProjectId !== 0 && TestRunStore.filterProjectId != null) || (TestRunStore.filteredTestSuiteId !== 0 && TestRunStore.filteredTestSuiteId != null) ? 'active' : 'inactive'}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        setProjectFilterOpen(!projectFilterOpen)
                      }}
                    />
                  }
                  content={
                    <div style={{ minWidth: '300px', padding: '12px' }} onClick={(e) => { e.stopPropagation() }}>
                      <div style={{ marginBottom: TestRunStore.filterProjectId !== 0 && TestRunStore.filterProjectId != null ? '12px' : '0' }}>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                          Project
                        </label>
                        <ProjectSelect
                          value={TestRunStore.filterProjectId ?? 0}
                          placeholder="Any Project"
                          onChange={onChangeProject}
                          allowOtherFramework={true}
                        />
                      </div>
                      {TestRunStore.filterProjectId !== 0 && TestRunStore.filterProjectId != null && (
                        <div>
                          <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                            Test Suite
                          </label>
                          <TestSuiteSelect
                            projectId={TestRunStore.filterProjectId ?? undefined}
                            value={TestRunStore.filteredTestSuiteId ?? 0}
                            placeholder="Any Test Suite"
                            onChange={onChangeTestSuite}
                          />
                        </div>
                      )}
                    </div>
                  }
                  on="click"
                  open={projectFilterOpen}
                  onClose={() => { setProjectFilterOpen(false) }}
                  onOpen={() => { setProjectFilterOpen(true) }}
                  position="bottom left"
                />
              </Table.HeaderCell>
              <Table.HeaderCell className={'score'}>Pass/Fail</Table.HeaderCell>
              <Table.HeaderCell className={'code-column'}>
                Code
                <Popup
                  trigger={
                    <Icon
                      name="filter"
                      className={`filter-icon ${TestRunStore.filterBranches?.length > 0 ? 'active' : 'inactive'}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        setBranchFilterOpen(!branchFilterOpen)
                      }}
                    />
                  }
                  content={
                    <div style={{ minWidth: '250px', padding: '8px' }}>
                      <BranchMultiSelect
                        value={TestRunStore.filterBranches}
                        placeholder={
                          (TestRunStore.filterProjectId === 0 || TestRunStore.filterProjectId == null) ||
                          (TestRunStore.filterProjectId !== 0 && TestRunStore.filterProjectId != null && !isBlank(ProjectStore.find(TestRunStore.filterProjectId)?.gitProvider))
                            ? 'Any Branch'
                            : 'No Repo Linked To Project'
                        }
                        onChange={onChangeBranch}
                      />
                    </div>
                  }
                  on="click"
                  open={branchFilterOpen}
                  onClose={() => { setBranchFilterOpen(false) }}
                  onOpen={() => { setBranchFilterOpen(true) }}
                  position="bottom left"
                />
              </Table.HeaderCell>
              <Table.HeaderCell className={'environment-column'}>
                Environment
                <Popup
                  trigger={
                    <Icon
                      name="filter"
                      className={`filter-icon ${TestRunStore.filterPipelineStageIds?.length > 0 || TestRunStore.filterEnvironmentIds?.length > 0 ? 'active' : 'inactive'}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        setPipelineStageFilterOpen(!pipelineStageFilterOpen)
                      }}
                    />
                  }
                  content={
                    <div style={{ minWidth: '300px', padding: '12px' }} onClick={(e) => { e.stopPropagation() }}>
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                          Pipeline Stage
                        </label>
                        <PipelineStageMultiSelect
                          value={TestRunStore.filterPipelineStageIds}
                          placeholder="Any Pipeline Stage"
                          onChange={onChangePipelineStage}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>
                          Environment
                        </label>
                        <EnvironmentMultiSelect
                          value={TestRunStore.filterEnvironmentIds}
                          placeholder="Any Environment"
                          onChange={onChangeEnvironment}
                        />
                      </div>
                    </div>
                  }
                  on="click"
                  open={pipelineStageFilterOpen}
                  onClose={() => { setPipelineStageFilterOpen(false) }}
                  onOpen={() => { setPipelineStageFilterOpen(true) }}
                  position="bottom left"
                />
              </Table.HeaderCell>
              <Table.HeaderCell className={'tagsAndFilters'}>Tags / Filters</Table.HeaderCell>
              <Table.HeaderCell className={'actions'}>
                Actions
                <Popup
                  trigger={
                    <Icon
                      name="filter"
                      className={`filter-icon ${TestRunStore.filterReviewerIds?.length > 0 ? 'active' : 'inactive'}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        setReviewerFilterOpen(!reviewerFilterOpen)
                      }}
                    />
                  }
                  content={
                    <div style={{ minWidth: '250px', padding: '8px' }}>
                      <TestRunReviewerSelect
                        value={TestRunStore.filterReviewerIds}
                        placeholder="Any Reviewer"
                        onChange={onChangeReviewer}
                      />
                    </div>
                  }
                  on="click"
                  open={reviewerFilterOpen}
                  onClose={() => { setReviewerFilterOpen(false) }}
                  onOpen={() => { setReviewerFilterOpen(true) }}
                  position="bottom left"
                />
                <Popup
                  trigger={
                    <Icon
                      name="x"
                      className="clear-filters-icon"
                      onClick={(e) => {
                        e.stopPropagation()
                        clearFilters()
                      }}
                    />
                  }
                  content="Clear filters"
                  on="hover"
                  position="bottom center"
                />
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {TestRunStore.testRuns.length === 0
              ? (
                  TestRunStore.loading
                    ? (
                <Table.Row>
                  <Table.Cell colSpan={9} style={{ textAlign: 'center', padding: '40px' }}>
                    <Spinner size={16} />
                  </Table.Cell>
                </Table.Row>
                      )
                    : (
                <Table.Row>
                  <Table.Cell colSpan={9}>
                    <div className="no-test-runs-message">
                      <h2>No test runs found</h2>
                      {AppStore.account.isLegacyOrTierTwoPlusPlan() === true && <div>{newTestRunButton}</div>}
                      {(allowUploadEnabled || AppStore.account.isLegacyOrTierTwoPlusPlan() !== true) && (
                        <div className="upload-button-container">{uploadTestRunButton}</div>
                      )}
                    </div>
                  </Table.Cell>
                </Table.Row>
                      )
                )
              : (
                  tableBody
                )}
          </Table.Body>

          <Table.Footer fullWidth={true}>
            <Table.Row>
              <Table.Cell colSpan={9}>
                <div className="pagination-footer">
                  {activePage > 1 && (
                    <IconButton marginRight={0} icon={'chevron left'} onClick={async () => { await handlePageChange(-1) }} />
                  )}
                  <div className="pagination-page-number">Page {activePage}</div>
                  {TestRunStore.testRuns.length > 0 && (
                    <IconButton marginRight={0} icon={'chevron right'} onClick={async () => { await handlePageChange(1) }} />
                  )}
                  <Button className="pagination-reset" disabled={activePage === 1} onClick={() => { void handlePageChange(0) }}>
                    Reset to page 1
                  </Button>
                </div>
              </Table.Cell>
            </Table.Row>
          </Table.Footer>
        </Table>
      </div>
      <div className="mobile-test-run-cards">
        {TestRunStore.loading && TestRunStore.testRuns.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px' }}><Spinner size={16} /></div>
        )}
        {!TestRunStore.loading && TestRunStore.testRuns.length === 0 && (
          <div className="no-test-runs-message">
            <h2>No test runs found</h2>
            {AppStore.account.isLegacyOrTierTwoPlusPlan() === true && <div>{newTestRunButton}</div>}
          </div>
        )}
        {TestRunStore.testRuns.map((testRun) => {
          const project = testRun.project
          const completed = completedTestRunStatuses.includes(testRun.status)
          const canceled = testRun.status === TestRunStatus.CANCELED
          const linkToTestRun = organizationUrl(`test-runs/${testRun.id}`)
          const linkToEnvironment = organizationUrl(`environments/${testRun.environment?.id}`)
          const isExpanded = expandedCards.has(testRun.id)

          let statusClass = ''
          if (testRun.status === TestRunStatus.PASS) statusClass = 'pass'
          if (testRun.status === TestRunStatus.FAIL) statusClass = 'fail'

          return (
            <div key={testRun.id} className={`mobile-card ${statusClass}`}>
              <div className="mobile-card-header" onClick={() => { toggleCardExpanded(testRun.id) }}>
                <div className={`mobile-card-status-bar ${statusClass}`} />
                <div className="mobile-card-summary">
                  <div className="mobile-card-top-row">
                    <span className={`mobile-card-status status-${testRun.status?.toLowerCase()}`}>
                      {testRun.status === TestRunStatus.RUNNING && <Spinner noText={true} size={14} />}
                      {testRun.statusDisplay}
                    </span>
                    <span className="mobile-card-date">
                      <DatetimeDisplay datetime={testRun.createdAt} />
                    </span>
                  </div>
                  <div className="mobile-card-info-row">
                    {project != null && <span className="mobile-card-project">{project.name}</span>}
                    {testRun.testSuite != null && <span className="mobile-card-suite">{testRun.testSuite.name}</span>}
                  </div>
                  <div className="mobile-card-progress-row">
                    <TestRunProgress
                      pass={testRun.passCount}
                      fail={testRun.failCount}
                      total={testRun.totalCount - testRun.ignoredCount}
                      showPassPercentage={false}
                    />
                    {completed && !canceled && testRun.totalCount > 0 && (
                      <span className="mobile-card-pct">
                        {Math.floor((testRun.passCount * 100) / (testRun.totalCount - testRun.ignoredCount))}%
                      </span>
                    )}
                  </div>
                </div>
                <Icon name={isExpanded ? 'chevron up' : 'chevron down'} className="mobile-card-chevron" />
              </div>
              {isExpanded && (
                <div className="mobile-card-details">
                  <div className="mobile-card-detail-row">
                    <span className="mobile-card-label">Duration</span>
                    <span><DurationDisplay duration={calculateDuration(testRun.startTime, testRun.endTime)} /></span>
                  </div>
                  {!isBlank(testRun.branch ?? '') && (
                    <div className="mobile-card-detail-row">
                      <span className="mobile-card-label">Branch</span>
                      <span>{testRun.branch}</span>
                    </div>
                  )}
                  {!isBlank(testRun.environmentName ?? '') && (
                    <div className="mobile-card-detail-row">
                      <span className="mobile-card-label">Environment</span>
                      <Link onClick={(e) => { e.stopPropagation() }} to={linkToEnvironment}>{testRun.environmentName}</Link>
                    </div>
                  )}
                  {testRun.build != null && (
                    <div className="mobile-card-detail-row">
                      <span className="mobile-card-label">Build</span>
                      <span>{testRun.build.name}</span>
                    </div>
                  )}
                  <div className="mobile-card-actions">
                    <Button size="small" onClick={() => { history.push(linkToTestRun) }}>
                      <Icon name="eye" /> View
                    </Button>
                    {AppStore.account.isLegacyOrTierTwoPlusPlan() === true && completed && (
                      <Button size="small" onClick={() => {
                        void TestRunStore.rerunTestRun(testRun)
                        void handlePageChange(0)
                      }}>
                        <Icon name="redo" /> Rerun
                      </Button>
                    )}
                    {AppStore.account.isLegacyOrTierTwoPlusPlan() === true && !completed && (
                      <Button size="small" onClick={async () => { await TestRunStore.cancelTestRun(testRun) }}>
                        <Icon name="stop circle outline" /> Cancel
                      </Button>
                    )}
                    <a href={linkToTestRun} target="_blank" rel="noreferrer">
                      <Button size="small"><Icon name="external alternate" /> New Tab</Button>
                    </a>
                  </div>
                </div>
              )}
            </div>
          )
        })}
        {TestRunStore.testRuns.length > 0 && (
          <div className="pagination-footer">
            {activePage > 1 && (
              <IconButton marginRight={0} icon={'chevron left'} onClick={async () => { await handlePageChange(-1) }} />
            )}
            <div className="pagination-page-number">Page {activePage}</div>
            {TestRunStore.testRuns.length > 0 && (
              <IconButton marginRight={0} icon={'chevron right'} onClick={async () => { await handlePageChange(1) }} />
            )}
          </div>
        )}
      </div>
      {showHelp && (
        <div className="keyboard-help-overlay" onClick={() => { setShowHelp(false) }}>
          <div className="keyboard-help-modal" onClick={(e) => { e.stopPropagation() }}>
            <h2>Keyboard Shortcuts</h2>
            <div className="keyboard-shortcuts">
              <h3>Navigation</h3>
              <div className="shortcut-row">
                <kbd>j</kbd>
                <span>Select next test run</span>
              </div>
              <div className="shortcut-row">
                <kbd>k</kbd>
                <span>Select previous test run</span>
              </div>
              <div className="shortcut-row">
                <kbd>v</kbd>
                <span>Open selected test run</span>
              </div>
              <div className="shortcut-row">
                <kbd>Enter</kbd>
                <span>Open selected test run</span>
              </div>
              <h3>Actions</h3>
              <div className="shortcut-row">
                <kbd>Ctrl</kbd>
                <kbd>r</kbd>
                <span>Re-run selected test run</span>
              </div>
              <div className="shortcut-row">
                <kbd>a</kbd>
                <span>Assign reviewer to selected test run</span>
              </div>
              <h3>Global</h3>
              <div className="shortcut-row">
                <kbd>g</kbd>
                <kbd>d</kbd>
                <span>Go to Dashboard</span>
              </div>
              <div className="shortcut-row">
                <kbd>g</kbd>
                <kbd>t</kbd>
                <span>Go to Test Runs</span>
              </div>
              <div className="shortcut-row">
                <kbd>m</kbd>
                <span>Toggle sidebar</span>
              </div>
              <div className="shortcut-row">
                <kbd>?</kbd>
                <span>Show this help menu</span>
              </div>
              <div className="shortcut-row">
                <kbd>Esc</kbd>
                <span>Close help menu</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </Page>
  )
})

export default TestRunsPage
